import { useEffect, useRef } from 'react';
import { ballColor, ballColorDark, ringWidth } from '@photo-merge/shared';
import { placeImage } from './crop';
import type { Photo } from './photos';

interface BallPreviewProps {
  photo: Photo;
  rank: number;
  count: number;
  /** Rendered diameter in CSS pixels. */
  size: number;
  className?: string;
}

/**
 * One cropped circle, drawn exactly the way the game renderer draws it, so what you
 * arrange is what you play.
 */
export function BallPreview({ photo, rank, count, size, className }: BallPreviewProps) {
  const ref = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 3);
    canvas.width = Math.round(size * dpr);
    canvas.height = Math.round(size * dpr);

    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, size, size);

    const ring = ringWidth(size / 2);
    const inner = size / 2 - ring / 2;

    ctx.save();
    ctx.beginPath();
    ctx.arc(size / 2, size / 2, inner, 0, Math.PI * 2);
    ctx.clip();
    // The crop is stored in size-independent units, so the same transform that drives
    // the editor and the export drives this preview too.
    const inset = ring / 2;
    const p = placeImage(photo.width, photo.height, inner * 2, photo.crop);
    ctx.drawImage(photo.image, p.x + inset, p.y + inset, p.width, p.height);
    ctx.restore();

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, inner, 0, Math.PI * 2);
    ctx.strokeStyle = ballColor(rank, count);
    ctx.lineWidth = ring;
    ctx.stroke();

    ctx.beginPath();
    ctx.arc(size / 2, size / 2, size / 2 - ring * 0.14, 0, Math.PI * 2);
    ctx.strokeStyle = ballColorDark(rank, count);
    ctx.lineWidth = Math.max(1, ring * 0.28);
    ctx.stroke();
  }, [photo, photo.crop, rank, count, size]);

  return (
    <canvas
      ref={ref}
      className={className}
      style={{ width: size, height: size, display: 'block' }}
      aria-hidden="true"
    />
  );
}
